import React, { useCallback, useRef, useMemo, useState, useEffect, Suspense } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

// Lazy load AceEditor to avoid loading it for nodes that don't need code editing.
// ext-language_tools must load AFTER ace-core (react-ace) — it registers via
// ace.define and will throw if ace-core isn't initialized yet. Awaiting it here
// also lets consumers set enableLiveAutocompletion without importing it themselves.
const AceEditor = React.lazy(() =>
  import('react-ace').then(async (mod) => {
    // Pre-load both themes so Ace can switch without flicker
    import('ace-builds/src-noconflict/theme-github');
    import('ace-builds/src-noconflict/theme-monokai');
    await import('ace-builds/src-noconflict/ext-language_tools');
    return mod;
  })
);

/**
 * NodeCodeEditor — AceEditor wrapper for code, cURL, JSON editing on nodes.
 * Lazy-loaded to avoid performance impact on nodes that don't use it.
 *
 * Usage:
 *   <NodeCodeEditor
 *     label="Code"
 *     value={code}
 *     onChange={(val) => setCode(val)}
 *     language="javascript"
 *     height="200px"
 *   />
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 4,
  right: 4,
  zIndex: 2,
  padding: 4,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadiusSm,
  opacity: 0.85,
  '&:hover': {
    opacity: 1,
    backgroundColor: theme.palette.background.paper,
  },
}));

const FullscreenBackdrop = styled('div')(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: theme.zIndex.modal - 1,
  backgroundColor: 'rgba(0, 0, 0, 0.48)',
}));

const EditorShell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ theme, isExpanded }) => ({
  position: 'relative',
  width: '100%',
  ...(isExpanded && {
    position: 'fixed',
    top: '5vh',
    left: '50%',
    zIndex: theme.zIndex.modal,
    display: 'flex',
    flexDirection: 'column',
    width: 'min(1200px, calc(100vw - 48px))',
    height: '90vh',
    maxHeight: '90vh',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[24],
    transform: 'translateX(-50%)',
  }),
}));

const FullscreenHeader = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ theme, isExpanded }) => ({
  display: isExpanded ? 'flex' : 'none',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  padding: `${NODE_UI.spacing.sm} ${NODE_UI.spacing.md}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const FullscreenTitle = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.primary,
}));

const EditorContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})(({ theme, isExpanded }) => ({
  position: 'relative',
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadiusSm,
  overflow: 'hidden',
  ...(isExpanded && {
    flex: 1,
    minHeight: 0,
    border: 0,
    borderRadius: 0,
  }),
  '& .ace_editor, & .ace_editor *': {
    fontFamily: '"Monaco", "Menlo", "Consolas", "Courier New", monospace !important',
    fontSize: '12px !important',
    lineHeight: 'normal !important',
  },
}));

const LoadingFallback = styled('div', {
  shouldForwardProp: (prop) => prop !== 'height',
})(({ theme, height }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height,
  padding: NODE_UI.spacing.xl,
  color: theme.palette.text.secondary,
  fontSize: NODE_UI.fontSize.value,
  gap: NODE_UI.spacing.sm,
}));

const NodeCodeEditor = React.memo(function NodeCodeEditor({
  label,
  value = '',
  onChange,
  language = 'javascript',
  height = '180px',
  readOnly = false,
  placeholder = '',
  showGutter = true,
  showPrintMargin = false,
  wrapEnabled = true,
  enableLiveAutocompletion = false,
  onLoad,
  sx,
  disableExpand = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const editorRef = useRef(null);
  const editorId = useMemo(() => `node-code-editor-${Math.random().toString(36).slice(2, 8)}`, []);
  const titleId = `${editorId}-fullscreen-title`;
  const [isExpanded, setIsExpanded] = useState(false);
  const editorHeight = isExpanded ? '100%' : height;

  const handleChange = useCallback((newValue) => {
    onChange?.(newValue);
  }, [onChange]);

  const openExpand = useCallback((e) => {
    e?.stopPropagation();
    setIsExpanded(true);
  }, []);

  const closeExpand = useCallback((e) => {
    e?.stopPropagation();
    setIsExpanded(false);
  }, []);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const closeOnEscape = (e) => {
      if (e.key === 'Escape') {
        setIsExpanded(false);
      }
    };

    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [isExpanded]);

  useEffect(() => {
    const resizeId = window.requestAnimationFrame(() => {
      editorRef.current?.editor?.resize();
    });

    return () => window.cancelAnimationFrame(resizeId);
  }, [isExpanded]);

  const aceEditor = (
    <Suspense
      fallback={
        <LoadingFallback height={editorHeight}>
          <CircularProgress size={14} />
          Loading editor...
        </LoadingFallback>
      }
    >
      <AceEditor
        ref={editorRef}
        mode={language}
        theme={isDark ? 'monokai' : 'github'}
        value={value}
        onChange={handleChange}
        onLoad={onLoad}
        name={editorId}
        width="100%"
        height={editorHeight}
        fontSize={12}
        showGutter={showGutter}
        showPrintMargin={showPrintMargin}
        wrapEnabled={wrapEnabled}
        readOnly={readOnly}
        placeholder={placeholder}
        editorProps={{ $blockScrolling: true }}
        setOptions={{
          useWorker: false,
          tabSize: 2,
          fontFamily: 'Monaco, Menlo, Consolas, Courier New, monospace',
          enableBasicAutocompletion: true,
          enableLiveAutocompletion,
          showLineNumbers: showGutter,
        }}
      />
    </Suspense>
  );

  return (
    <div
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
      onWheel={stopPropagation}
    >
      {label && <Label>{label}</Label>}
      {isExpanded && <FullscreenBackdrop onClick={closeExpand} />}
      <EditorShell
        isExpanded={isExpanded}
        role={isExpanded ? 'dialog' : undefined}
        aria-modal={isExpanded ? 'true' : undefined}
        aria-labelledby={isExpanded ? titleId : undefined}
      >
        <FullscreenHeader isExpanded={isExpanded}>
          <FullscreenTitle id={titleId}>{label || 'Code editor'}</FullscreenTitle>
          <Tooltip title="Exit fullscreen">
            <IconButton size="small" onClick={closeExpand} aria-label="Exit fullscreen">
              <FullscreenExitIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </FullscreenHeader>
        <EditorContainer isExpanded={isExpanded} sx={sx}>
          {aceEditor}
          {!disableExpand && !isExpanded && (
            <Tooltip title="Expand editor" placement="left">
              <ExpandButton size="small" onClick={openExpand} aria-label="Expand editor">
                <FullscreenIcon sx={{ fontSize: 16 }} />
              </ExpandButton>
            </Tooltip>
          )}
        </EditorContainer>
      </EditorShell>
    </div>
  );
});

export default NodeCodeEditor;
